import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Button,
  Table,
  Pagination,
  Form,
  InputGroup,
} from "react-bootstrap";
import { useAuthState } from "../../context/AuthContext";
import { loadAllUsers } from "../../lib/api/user";
import globalVar from "../../lib/util/global";
import { EmptyBlank } from "../../components/common";

// ---------------------------------------------------------------
//
//  Pagination Component
//
// ---------------------------------------------------------------
const PaginationComponent = ({
  total = 0,
  itemsPerPage = 10,
  currentPage = 1,
  onPageChange,
}) => {
  const [totalPages, setTotalPages] = useState(0);
  useEffect(() => {
    if (total > 0 && itemsPerPage > 0)
      setTotalPages(Math.ceil(total / itemsPerPage));
  }, [total, itemsPerPage]);

  const paginationItems = useMemo(() => {
    const pages = [];

    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => onPageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return pages;
  }, [totalPages, currentPage, onPageChange]);

  if (totalPages === 0) return null;

  return (
    <Pagination>
      {/* <Pagination.First
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
      /> */}
      <Pagination.Prev
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />
      {paginationItems}
      <Pagination.Next
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
      {/* <Pagination.Last
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
      /> */}
    </Pagination>
  );
};

// ---------------------------------------------------------------
//
//  Account Component
//
// ---------------------------------------------------------------
const AccountComponent = ({
  num,
  _id,
  courseIds,
  username,
  displayName,
  roles,
  dsUnivLink,
  createdAt,
  updatedAt,
  onClickEdit,
  onClickRemove,
}) => {
  return (
    <tr>
      <td>{num}</td>
      <td>{displayName}</td>
      <td>{roles}</td>
      <td>{dsUnivLink ? "on" : "off"}</td>
      <td>{new Date(createdAt).toLocaleDateString()}</td>
      <td>
        {updatedAt
          ? new Date(updatedAt).toLocaleString()
          : new Date(createdAt).toLocaleDateString()}
      </td>
      <td>
        <Button onClick={onClickEdit} id={_id} size="sm">
          수정
        </Button>
      </td>
      <td>
        <Button onClick={onClickRemove} id={_id} size="sm" variant="warning">
          삭제
        </Button>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------
//
//  Search Component
//
// ---------------------------------------------------------------
const SearchComponent = ({ filterName, isAdmin, onClickSearch }) => {
  const [newFilterName, setNewFilterName] = useState(filterName);

  const onChangeSearch = (e) => {
    setNewFilterName(e.target.value);
  };

  return (
    <Fragment>
      <InputGroup className="mb-3">
        <InputGroup.Text>검색</InputGroup.Text>
        <Form.Control
          placeholder={
            isAdmin
              ? "사번/학번/역할 또는 이름을 입력하세요."
              : "학번 또는 이름을 입력하세요."
          }
          onChange={onChangeSearch}
          defaultValue={newFilterName}
        />
        <Button onClick={() => onClickSearch(newFilterName)}>적용</Button>
      </InputGroup>
    </Fragment>
  );
};

// ---------------------------------------------------------------
//
//  Home
//
// ---------------------------------------------------------------
const Home = () => {
  const ITEMS_PER_PAGE = 100;

  const navigate = useNavigate();
  const {
    loggedUser: { roler },
  } = useAuthState();

  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [filterName, setFilterName] = useState(globalVar.filterName);

  const requestAllUsers = useCallback(async () => {
    const { status, users } = await loadAllUsers();
    //console.log("status => ", status);
    //console.log("users => ", users);
    setUsers(users);
  }, []);

  useEffect(() => {
    requestAllUsers();
  }, [requestAllUsers]);

  const handleEdit = (e) => {
    const { id } = e.target;
    const user = users.find((x) => x._id === id);
    if (user) {
      //console.log(user);
      navigate("/accounts/edit", {
        state: {
          user: user,
        },
      });
    }
  };

  const handleRemove = () => {
    if (window.confirm("정말 삭제하시겠습니까?")) {
      //      onRemove(originData.id);
      alert("현재는 지원하지 않습니다.");
    }
  };

  const handleNew = () => {
    alert("현재는 지원하지 않습니다.");
    //console.log("handleNew --- start");
    //navigate("/accounts/new");
  };

  const handleSearch = (value) => {
    console.log("handleSearch : ", value);
    setFilterName(value);
    globalVar.filterName = value;
  };

  const filtered = useMemo(() => {
    let filteredResult = users.filter((x) => {
      let valid = true;

      if (filterName) {
        if (
          !(
            x.username.includes(filterName) ||
            x.displayName.includes(filterName) ||
            x.roles.includes(filterName)
          )
        ) {
          valid = false;
        }
      }

      return valid;
    });
    setTotalItems(filteredResult.length);

    return filteredResult.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [users, currentPage, filterName]);

  return (
    <div>
      <Container fluid="md">
        <SearchComponent
          isAdmin={roler === "admin"}
          filterName={filterName}
          onClickSearch={handleSearch}
        />
        <Table variant="striped">
          <thead>
            <tr>
              <th>#</th>
              <th>사용자 이름</th>
              <th>역할</th>
              <th>연동</th>
              <th>가입일</th>
              <th>수정일</th>
              <th style={{ visibility: "hidden" }}>수정</th>
              <th style={{ visibility: "hidden" }}>삭제</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it, index) => (
              <AccountComponent
                key={index}
                {...it}
                num={index + 1 + (currentPage - 1) * ITEMS_PER_PAGE}
                onClickEdit={handleEdit}
                onClickRemove={handleRemove}
              />
            ))}
          </tbody>
        </Table>
        <br />
        <div className="d-flex justify-content-center">
          <PaginationComponent
            total={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
        <br />
        <Button variant="secondary" type="click" onClick={handleNew}>
          추가
        </Button>
        <EmptyBlank />
      </Container>
    </div>
  );
};

export default Home;
